
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, AlertCircle, Settings, BatteryCharging, Sun, Zap, ArrowUpDown, ChevronUp, ChevronDown, RefreshCw } from "lucide-react";

const MonitoramentoPage = () => {
  return (
    <Layout>
      <div className="container mx-auto py-10">
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-3xl font-bold">Monitoramento de Energia</h1>
            <p className="text-muted-foreground">
              Acompanhe em tempo real o desempenho dos seus sistemas de energia.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Dados atualizados às 16:50 em 09 de Abril, 2025
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Link to="/dashboard">
              <Button variant="outline" size="sm">
                Dashboard Geral
              </Button>
            </Link>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="devices">Dispositivos</TabsTrigger>
            <TabsTrigger value="alerts">Alertas</TabsTrigger>
            <TabsTrigger value="settings">Configurações</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Activity className="mr-2 h-5 w-5 text-blue-500" />
                    Consumo Atual
                  </CardTitle>
                  <CardDescription>Consumo total de energia neste momento.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">-</div>
                  <p className="text-sm text-muted-foreground">
                    Atualizado há 5 minutos
                  </p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Sun className="mr-2 h-5 w-5 text-yellow-500" />
                    Geração Solar
                  </CardTitle>
                  <CardDescription>Energia gerada pelos painéis solares.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">-</div>
                  <p className="text-sm text-muted-foreground">
                    Pico de geração hoje: -
                  </p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <BatteryCharging className="mr-2 h-5 w-5 text-green-500" />
                    Bateria
                  </CardTitle>
                  <CardDescription>Nível de carga da bateria.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">-</div>
                  <p className="text-sm text-muted-foreground">
                    Tempo restante estimado: -
                  </p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar
                  </Button>
                </CardFooter>
              </Card>
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-xl font-semibold">
                  Gráfico de Consumo
                </CardTitle>
                <CardDescription>
                  Acompanhe o consumo de energia ao longo do tempo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center text-muted-foreground py-8">
                  <Activity className="h-10 w-10 mx-auto mb-4 text-muted" />
                  <p>Gráfico de consumo será implementado em breve.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="devices">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Zap className="mr-2 h-5 w-5 text-blue-500" />
                    Inversor Solar
                  </CardTitle>
                  <CardDescription>Informações do inversor solar.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Modelo:</strong> -</p>
                  <p><strong>Status:</strong> -</p>
                  <p><strong>Temperatura:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <BatteryCharging className="mr-2 h-5 w-5 text-green-500" />
                    Bateria
                  </CardTitle>
                  <CardDescription>Informações da bateria.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Modelo:</strong> -</p>
                  <p><strong>Capacidade:</strong> -</p>
                  <p><strong>Carga:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <ArrowUpDown className="mr-2 h-5 w-5 text-purple-500" />
                    Medidor de Energia
                  </CardTitle>
                  <CardDescription>Informações do medidor de energia.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Modelo:</strong> -</p>
                  <p><strong>Importação:</strong> -</p>
                  <p><strong>Exportação:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="alerts">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold flex items-center">
                    <AlertCircle className="mr-2 h-4 w-4 text-red-500" />
                    Alta Temperatura do Inversor
                  </CardTitle>
                  <CardDescription>
                    O inversor está operando em uma temperatura elevada.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Data/Hora:</strong> -</p>
                  <p><strong>Temperatura:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold flex items-center">
                    <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />
                    Baixa Geração Solar
                  </CardTitle>
                  <CardDescription>
                    A geração solar está abaixo do esperado.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Data/Hora:</strong> -</p>
                  <p><strong>Geração Atual:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-semibold flex items-center">
                    <AlertCircle className="mr-2 h-4 w-4 text-blue-500" />
                    Bateria Descarregada
                  </CardTitle>
                  <CardDescription>
                    A bateria atingiu um nível de carga muito baixo.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p><strong>Data/Hora:</strong> -</p>
                  <p><strong>Carga Atual:</strong> -</p>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="ghost" size="sm">
                    Ver Detalhes
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="settings">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Settings className="mr-2 h-5 w-5 text-gray-500" />
                    Configurações Gerais
                  </CardTitle>
                  <CardDescription>
                    Ajuste as configurações do sistema de monitoramento.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p>Notificações por Email</p>
                      <Button variant="outline" size="sm">
                        Ativar
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <p>Intervalo de Atualização</p>
                      <select className="border rounded px-2 py-1">
                        <option>5 minutos</option>
                        <option>10 minutos</option>
                        <option>15 minutos</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button variant="default" size="sm">
                    Salvar
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default MonitoramentoPage;
